// features/pos/components/POSHeader.tsx
// ENHANCED - Better Visual Hierarchy, Quick Actions, Tooltips, Status Indicators

import { useState } from 'react';
import { usePosStore } from '@/store/posStore';
import {
    Clock, Store, User, Wifi, WifiOff, ChefHat, Monitor,
    Building2, DoorOpen, RotateCcw, ArrowUpRight
} from 'lucide-react';
import { clsx } from 'clsx';
import { motion } from 'framer-motion';

interface POSHeaderProps {
    shift: any;
    branches: any[];
    selectedBranchId: string;
    onBranchChange?: (id: string) => void;  // ✅ Made optional
    onOpenShift: () => void;
    onCloseShift?: () => void;
    isOnline: boolean;
    showCustomerDisplay: boolean;
    onToggleCustomerDisplay: () => void;
    onToggleKitchenView: () => void;
    isKitchenView: boolean;
    onCustomerClick: () => void;
    lastSaleId?: string;
    onVoidLastSale?: () => void;
    onViewActiveShift?: () => void;
}

export function POSHeader({
    shift, branches, selectedBranchId, onBranchChange, onOpenShift, onCloseShift,
    isOnline, showCustomerDisplay, onToggleCustomerDisplay,
    onToggleKitchenView, isKitchenView, onCustomerClick,
    lastSaleId, onVoidLastSale, onViewActiveShift,
}: POSHeaderProps) {
    const pos = usePosStore();

    const formatTime = (dateStr: string) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDuration = (dateStr: string) => {
        if (!dateStr) return '';
        const start = new Date(dateStr).getTime();
        const now = Date.now();
        const diff = now - start;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const groupedBranches = branches.reduce((acc: Record<string, any[]>, branch: any) => {
        const businessName = branch.businessName || 'General';
        if (!acc[businessName]) acc[businessName] = [];
        acc[businessName].push(branch);
        return acc;
    }, {});

    const businessEntries = Object.entries(groupedBranches);
    const hasMultipleBusinesses = businessEntries.length > 1;
    const hasBranches = branches && branches.length > 0;

    return (
        <div className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between gap-4 flex-shrink-0 z-20">
            {/* LEFT SECTION - Status & Controls */}
            <div className="flex items-center gap-2">
                {/* Online Status */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={clsx(
                        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold border transition-all',
                        isOnline
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-amber-50 text-amber-700 border-amber-200'
                    )}
                    title={isOnline ? 'Connected to server' : 'Working offline - data will sync later'}
                >
                    {isOnline ? (
                        <Wifi size={12} className="text-emerald-500" />
                    ) : (
                        <WifiOff size={12} className="text-amber-500 animate-pulse" />
                    )}
                    <span className="hidden sm:inline">{isOnline ? 'Online' : 'Offline'}</span>
                </motion.div>

                {/* Shift Status */}
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={!pos.activeShiftId ? onOpenShift : (onViewActiveShift || onCloseShift)}
                    className={clsx(
                        'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all border',
                        pos.activeShiftId
                            ? 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 border-emerald-200 hover:from-emerald-100 hover:to-teal-100 cursor-pointer shadow-sm'
                            : 'bg-gradient-to-r from-rose-50 to-red-50 text-rose-700 border-rose-200 hover:from-rose-100 hover:to-red-100 cursor-pointer shadow-sm'
                    )}
                    type="button"
                    title={pos.activeShiftId ? `Shift open since ${formatTime(shift?.openedAt)} (Click for shift options)` : 'Click to open shift'}
                >
                    <div className="relative">
                        <div className={clsx(
                            'w-2.5 h-2.5 rounded-full',
                            pos.activeShiftId ? 'bg-emerald-500' : 'bg-rose-500'
                        )} />
                        {pos.activeShiftId && (
                            <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping opacity-75" />
                        )}
                    </div>
                    {pos.activeShiftId ? (
                        <div className="flex items-center gap-1.5">
                            <span className="hidden sm:inline">
                                {formatDuration(shift?.openedAt)}
                            </span>
                            <span className="text-[10px] text-emerald-500 font-mono">
                                {formatTime(shift?.openedAt)}
                            </span>
                        </div>
                    ) : (
                        <span>Open Shift</span>
                    )}
                </motion.button>

                {/* Close Shift Button */}
                {pos.activeShiftId && onCloseShift && (
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={onCloseShift}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-orange-200 text-orange-700 rounded-full text-xs font-bold hover:bg-orange-50 hover:border-orange-300 transition-all shadow-sm"
                        type="button"
                        title="Close shift and reconcile transactions"
                    >
                        <DoorOpen size={14} />
                        <span className="hidden sm:inline">Close</span>
                    </motion.button>
                )}

                {/* Void Last Sale - Quick Action */}
                {lastSaleId && onVoidLastSale && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={onVoidLastSale}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-rose-200 text-rose-700 rounded-full text-xs font-bold hover:bg-rose-50 hover:border-rose-300 transition-all shadow-sm"
                        type="button"
                        title="Void the last completed sale"
                    >
                        <RotateCcw size={13} />
                        <span className="hidden sm:inline">Void</span>
                    </motion.button>
                )}

                {/* Branch Display - Read only for cashiers (no onBranchChange) */}
                {hasBranches && !onBranchChange && (
                    <div className={clsx(
                        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border',
                        'bg-slate-50 border-slate-200'
                    )}>
                        <Store size={13} className="text-slate-500 flex-shrink-0" />
                        <span className="text-xs font-semibold text-slate-700 max-w-[160px] truncate">
                            {branches.find((b: any) => b.id === selectedBranchId)?.name || 'Branch'}
                        </span>
                    </div>
                )}

                {/* Branch Selector - Only for managers/admins (with onBranchChange) */}
                {hasBranches && onBranchChange && (
                    <div className="flex items-center gap-2">
                        <div className={clsx(
                            'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all',
                            hasMultipleBusinesses
                                ? 'bg-purple-50 border-purple-200'
                                : 'bg-slate-50 border-slate-200'
                        )}>
                            {hasMultipleBusinesses ? (
                                <Building2 size={13} className="text-purple-500 flex-shrink-0" />
                            ) : (
                                <Store size={13} className="text-slate-500 flex-shrink-0" />
                            )}
                            <select
                                value={selectedBranchId}
                                onChange={(e) => onBranchChange(e.target.value)}
                                className="text-xs font-semibold bg-transparent text-slate-700 focus:outline-none max-w-[160px] truncate cursor-pointer"
                                title="Select branch location"
                            >
                                {hasMultipleBusinesses ? (
                                    businessEntries.map(([businessName, bizBranches]) => (
                                        <optgroup key={businessName} label={`🏢 ${businessName}`}>
                                            {bizBranches.map((b: any) => (
                                                <option key={b.id} value={b.id}>
                                                    {b.isMainBranch ? '⭐ ' : '  '}{b.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))
                                ) : (
                                    branches.map((b: any) => (
                                        <option key={b.id} value={b.id}>
                                            {b.isMainBranch ? '⭐ ' : ''}{b.name}
                                        </option>
                                    ))
                                )}
                            </select>
                            {hasMultipleBusinesses && (
                                <ArrowUpRight size={10} className="text-purple-400" />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* CENTER - Sale Type Selector */}
            <div className="flex items-center bg-slate-100 rounded-xl p-0.5 shadow-inner">
                {(['retail', 'wholesale', 'clearance'] as const).map(type => (
                    <motion.button
                        key={type}
                        onClick={() => pos.setSaleType(type)}
                        whileHover={pos.saleType !== type ? { scale: 1.05 } : {}}
                        whileTap={{ scale: 0.95 }}
                        className={clsx(
                            'relative px-4 py-2 rounded-lg text-xs font-bold capitalize transition-all',
                            pos.saleType === type
                                ? 'bg-white text-emerald-600 shadow-md'
                                : 'text-slate-500 hover:text-slate-700'
                        )}
                        type="button"
                    >
                        {pos.saleType === type && (
                            <motion.div
                                layoutId="saleTypeIndicator"
                                className="absolute inset-0 bg-white rounded-lg shadow-md"
                                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                            />
                        )}
                        <span className="relative z-10 flex items-center gap-1.5">
                            {type === 'retail' && '🏪'}
                            {type === 'wholesale' && '📦'}
                            {type === 'clearance' && '🏷️'}
                            {type}
                        </span>
                    </motion.button>
                ))}
            </div>

            {/* RIGHT SECTION - Actions */}
            <div className="flex items-center gap-2">
                {/* Kitchen View Toggle */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onToggleKitchenView}
                    className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border',
                        isKitchenView
                            ? 'bg-gradient-to-r from-red-50 to-orange-50 text-red-700 border-red-200 shadow-sm'
                            : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
                    )}
                    type="button"
                    title="Toggle kitchen display view"
                >
                    <ChefHat size={14} className={isKitchenView ? 'text-red-500' : 'text-slate-400'} />
                    <span className="hidden sm:inline">Kitchen</span>
                    {isKitchenView && (
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    )}
                </motion.button>

                {/* Customer Display Toggle */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onToggleCustomerDisplay}
                    className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border',
                        showCustomerDisplay
                            ? 'bg-gradient-to-r from-purple-50 to-violet-50 text-purple-700 border-purple-200 shadow-sm'
                            : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
                    )}
                    type="button"
                    title="Toggle customer-facing display"
                >
                    <Monitor size={14} className={showCustomerDisplay ? 'text-purple-500' : 'text-slate-400'} />
                    <span className="hidden sm:inline">Display</span>
                </motion.button>

                {/* Customer Button */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onCustomerClick}
                    className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border',
                        pos.customerName && pos.customerName !== 'Walk-in Customer'
                            ? 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 border-emerald-200 shadow-sm'
                            : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
                    )}
                    type="button"
                    title={pos.customerName ? `Customer: ${pos.customerName}` : 'Attach customer'}
                >
                    <div className="relative">
                        <User size={14} className={pos.customerName ? 'text-emerald-500' : 'text-slate-400'} />
                        {pos.customerName && pos.customerName !== 'Walk-in Customer' && (
                            <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        )}
                    </div>
                    <span className="hidden sm:inline truncate max-w-[100px]">
                        {pos.customerName || 'Customer'}
                    </span>
                    {pos.customerInfo && (
                        <span className="text-amber-600 text-[10px] font-bold bg-amber-100 px-1.5 py-0.5 rounded-full">
                            ⭐{pos.customerInfo.loyaltyPoints?.toLocaleString()}
                        </span>
                    )}
                </motion.button>
            </div>
        </div>
    );
}